/**
 * RequireAdmin — Cloudflare Access identity 게이트.
 *
 * 배포 환경:
 *   `admin.lashpic.com` 은 Cloudflare Zero Trust(팀 `maestiq`) 로 엣지 보호된다.
 *   앱이 로드됐다는 사실 자체가 CF Access Policy 통과를 의미.
 *
 * 본 컴포넌트의 역할:
 *   1) `/cdn-cgi/access/get-identity` 조회 → email/user_uuid 확보
 *   2) 실패 시(로컬 vite dev 등 보호되지 않은 환경) → `/403` 으로 리다이렉트
 *   3) 성공 시 anomaly 이벤트 emit (Phase 4.5 Worker 실구현 예정)
 *   4) Outlet 렌더
 *
 * allowlist 클라이언트 검증은 제거됨 — CF Access Policy 가 담당한다.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { emitLoginEvent } from './anomaly-hook';
import { useCfAccessIdentity } from './cf-access';

type GateState = 'loading' | 'unauthenticated' | 'ready';

interface RequireAdminProps {
  readonly children?: ReactNode;
  /** 인증 실패 시 이동할 경로 (기본 /403). */
  readonly forbiddenPath?: string;
}

export function RequireAdmin({ children, forbiddenPath = '/403' }: RequireAdminProps) {
  const { identity, isLoaded } = useCfAccessIdentity();
  const [state, setState] = useState<GateState>('loading');
  const anomalyEmittedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) {
      setState('loading');
      return;
    }
    if (!identity) {
      setState('unauthenticated');
      return;
    }
    setState('ready');

    // anomaly emit — 같은 세션에서 1회만.
    if (!anomalyEmittedRef.current) {
      anomalyEmittedRef.current = true;
      void emitLoginEvent(identity.user_uuid).catch((error) => {
        console.warn('[Lashpic Admin] anomaly emit 실패:', error);
      });
    }
  }, [isLoaded, identity]);

  if (state === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-dvh items-center justify-center text-sm text-slate-500"
      >
        인증 확인 중...
      </div>
    );
  }

  if (state === 'unauthenticated') {
    return <Navigate to={forbiddenPath} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
